<template>
  <div class="phist">
    <p v-if="!sku" class="phist-hint">{{ $t("priceHistory.noSku") }}</p>
    <p v-else-if="loading" class="phist-hint">{{ $t("priceHistory.loading") }}</p>
    <p v-else-if="error" class="phist-hint phist-hint--warn">{{ $t("priceHistory.loadError") }}</p>
    <p v-else-if="!history || !history.entries.length" class="phist-hint">{{ $t("priceHistory.none", { sku }) }}</p>
    <template v-else>
      <div class="phist-current">
        <div>
          <div class="phist-label">{{ $t("priceHistory.current") }}</div>
          <div class="phist-price">{{ eur(history.currentPrice) }}</div>
        </div>
        <div class="phist-meta">
          <div>{{ $t("priceHistory.since", { date: formatDateTime(history.currentSince) }) }}</div>
          <div>{{ $tc("priceHistory.changes", history.changes, { n: history.changes }) }}</div>
        </div>
      </div>

      <price-chart v-if="history.entries.length > 1" class="phist-chart" :entries="history.entries" :width="760" :height="200"></price-chart>

      <table class="phist-table">
        <thead>
          <tr>
            <th>{{ $t("priceHistory.date") }}</th>
            <th class="phist-num">{{ $t("priceHistory.price") }}</th>
            <th class="phist-num">{{ $t("priceHistory.change") }}</th>
            <th class="phist-num">%</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="e in history.entries" :key="e.recordedOn">
            <td>{{ formatDateTime(e.recordedOn) }}</td>
            <td class="phist-num">{{ eur(e.price) }}</td>
            <template v-if="e.isStart">
              <td class="phist-num phist-start" colspan="2">{{ $t("priceHistory.start") }}</td>
            </template>
            <template v-else>
              <td class="phist-num" :class="signClass(e.change)">{{ arrow(e.change) }} {{ signed(e.change) }} €</td>
              <td class="phist-num" :class="signClass(e.change)">{{ e.changePercent == null ? "–" : signed(e.changePercent) + " %" }}</td>
            </template>
          </tr>
        </tbody>
      </table>
      <p class="phist-hint">{{ $t("priceHistory.source") }}</p>
    </template>
  </div>
</template>

<script>
import PriceChart from "@/components/PriceChart.vue";
import priceHistoryService from "@/services/price-history.service";

/**
 * Preisverlauf einer SKU (Verkaufspreis brutto aus dem Afterbuy-Import) als Chart ab Aufzeichnung und Liste mit Aenderung in EUR und Prozent.
 * Die API liefert die Eintraege neueste zuerst.
 */
export default {
  name: "PriceHistory",
  components: { PriceChart },
  props: {
    sku: { type: String, default: "" },
  },
  data() {
    return { history: null, loading: false, error: false };
  },
  watch: {
    sku() {
      this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      this.history = null;
      this.error = false;
      if (!this.sku) return Promise.resolve();
      const requested = this.sku;
      this.loading = true;
      return priceHistoryService
        .getHistory(requested)
        .then((history) => {
          if (requested === this.sku) this.history = history;
        })
        .catch(() => {
          this.error = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fmt(value, decimals = 2) {
      return this.$options.filters.formatNumberGerman(value, decimals);
    },
    eur(value) {
      return value == null ? "–" : `${this.fmt(value)} €`;
    },
    signed(value) {
      return `${value > 0 ? "+" : ""}${this.fmt(value)}`;
    },
    arrow(value) {
      return value > 0 ? "▲" : value < 0 ? "▼" : "•";
    },
    signClass(value) {
      return value > 0 ? "phist-up" : value < 0 ? "phist-down" : "";
    },
    formatDateTime(value) {
      return value ? new Date(value).toLocaleString(this.$i18n.locale, { dateStyle: "short", timeStyle: "short" }) : "";
    },
  },
};
</script>

<style scoped>
.phist-hint {
  text-align: left;
  color: #777;
  font-size: 12px;
  margin: 8px 0;
}
.phist-hint--warn {
  color: #b26a00;
}
.phist-current {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin: 6px 0 14px;
  padding: 10px 14px;
  background: #f6f8f5;
  border-radius: 6px;
}
.phist-label {
  font-size: 11px;
  color: #777;
  text-transform: uppercase;
}
.phist-price {
  font-size: 24px;
  font-weight: 600;
}
.phist-meta {
  text-align: right;
  font-size: 12px;
  color: #555;
}
.phist-chart {
  margin: 0 0 14px;
}
.phist-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
/* Die globale Regel ".md-tabs-content table thead" blendet Tabellenkoepfe in Tabs aus */
.phist-table thead {
  display: table-header-group;
}
.phist-table th {
  font-size: 11px;
  color: #7f915a;
  text-align: left;
  padding: 6px 8px;
  border-bottom: 1px solid #e5e5e5;
}
.phist-table td {
  text-align: left;
  padding: 6px 8px;
  border-bottom: 1px solid #f0f0f0;
}
.phist-table th.phist-num,
.phist-table td.phist-num {
  text-align: right;
  white-space: nowrap;
}
.phist-start {
  color: #888;
  font-style: italic;
}
.phist-up {
  color: #7f915a;
}
.phist-down {
  color: #c62828;
}
</style>
